import { ChangeDetectionStrategy, Component, computed, input, signal } from "@angular/core";
import { NotificationService } from "../../services/notification.service";
import { StateService } from "../../services/state.service";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { IngestionPointCategory } from "./data.ingestion.const";
import { IngestionPoint } from "./data.ingestion.service";
import { copyIngestionForm } from "./ingestion.copy.form.util";
import { loadCopyableIngestionPoints } from "./ingestion.copyable.forms.util";
import { formatFormUseColumnText } from "./admin/ingestion.admin.list.util";
import { s25Resource } from "../../util/s25.resource";
import { S25Const } from "../../util/s25-const";
import { S25Util } from "../../util/s25-util";

@Component({
    selector: "s25-ng-ingestion-copyable-forms-section",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25LoadingInlineModule],
    template: `
        @if (copyableFormsResource.isLoading()) {
            <s25-ng-loading-inline-static />
        } @else if (copyableFormsResource.error()) {
            <p class="copy-forms-error">Unable to load forms available to copy.</p>
        } @else if (!copyableForms().length) {
            <p class="copy-forms-empty">No forms available to copy.</p>
        } @else {
            <table class="copy-forms-table">
                <thead>
                    <tr>
                        <th scope="col">Form Name</th>
                        <th scope="col">Form Use</th>
                        <th scope="col">Status</th>
                        <th scope="col"><span class="sr-only">Actions</span></th>
                    </tr>
                </thead>
                <tbody>
                    @for (form of copyableForms(); track form.ingestionPointId) {
                        <tr>
                            <td>{{ form.ingestionPointName }}</td>
                            <td>{{ formatFormUse(form.use) }}</td>
                            <td>{{ form.enabled ? "Enabled" : "Disabled" }}</td>
                            <td>
                                <button
                                    type="button"
                                    class="aw-button aw-button--outline"
                                    [disabled]="copyingFormId() === form.ingestionPointId"
                                    (click)="onCopyForm(form)"
                                >
                                    Copy
                                </button>
                            </td>
                        </tr>
                    }
                </tbody>
            </table>
        }
    `,
    styles: `
        :host {
            display: block;
            margin-top: 2rem;
        }

        .copy-forms-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 0.75rem;
        }

        .copy-forms-table th,
        .copy-forms-table td {
            padding: 0.5rem 0.75rem;
            border-bottom: 1px solid var(--color--border);
            text-align: left;
        }

        .copy-forms-empty,
        .copy-forms-error {
            color: var(--color--text-muted);
            margin-top: 0.5rem;
        }

        .sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            border: 0;
        }
    `,
})
export class IngestionCopyableFormsSectionComponent {
    readonly orgId = input.required<number>();

    readonly copyingFormId = signal<number | undefined>(undefined);

    readonly copyableFormsResource = s25Resource({
        loader: () => loadCopyableIngestionPoints(IngestionPointCategory.ENGAGEMENT),
    });

    readonly copyableForms = computed(() => this.copyableFormsResource.value() ?? []);

    readonly formatFormUse = formatFormUseColumnText;

    async onCopyForm(form: IngestionPoint): Promise<void> {
        if (!form.ingestionPointId || this.copyingFormId()) {
            return;
        }

        this.copyingFormId.set(form.ingestionPointId);

        try {
            const copiedForm = await copyIngestionForm(form.ingestionPointId, {
                targetSubCat: String(this.orgId()),
            });

            if (!copiedForm.ingestionPointId) {
                throw new Error("Failed to copy form: new form ID is missing");
            }

            NotificationService.post(
                `Copied "${copiedForm.ingestionPointName ?? "form"}". Opening Form Management…`,
                10 * S25Const.ms.sec,
            );
            await StateService.go("seForms", { ingestionPointId: String(copiedForm.ingestionPointId) }, null);
        } catch (error) {
            S25Util.showError(error);
        } finally {
            this.copyingFormId.set(undefined);
        }
    }
}
